/**
 * permissionConfig.js
 * Centralized permission system configuration for the frontend.
 * Mirrors the backend PermissionService constants.
 */

// ─── Role Hierarchy ────────────────────────────────────────────────────────────
// Lower rank = more authority. Must match backend ROLE_HIERARCHY.
export const ROLE_HIERARCHY = {
  organization_head: 0,
  admin: 1,
  finance_manager: 2,
  accountant: 2,
  project_manager: 2,
  employee: 3,
  viewer: 3,
  user: 4,
};

export const ROLE_LABELS = {
  organization_head: 'Organization Head',
  admin: 'Admin',
  finance_manager: 'Finance Manager',
  accountant: 'Accountant',
  project_manager: 'Project Manager',
  employee: 'Employee',
  viewer: 'Viewer',
  user: 'User',
};

export const ROLE_DETAILS = {
  organization_head: {
    note: 'Full control over all organization data, members, roles, and settings. Cannot be restricted by any other role.',
    modules: ['Dashboard', 'Users', 'Roles', 'Permissions', 'Projects', 'Transactions', 'Reports', 'Analytics', 'Categories', 'Persons', 'Invitations', 'Settings', 'Exports', 'Audit', 'Project Finance'],
    managed_by: null,
    color: '#7c3aed',
  },
  admin: {
    note: 'Operational controller for the organization. Can manage most features and configure permissions for lower roles. Restricted from modifying Organization Head settings.',
    modules: ['Dashboard', 'Users', 'Roles', 'Permissions', 'Projects', 'Transactions', 'Reports', 'Analytics', 'Categories', 'Persons', 'Invitations', 'Settings', 'Exports', 'Audit'],
    managed_by: 'Organization Head',
    color: '#c44569',
  },
  finance_manager: {
    note: 'Oversees financial monitoring, approvals, and reporting. Can view and approve financial transactions but cannot manage users or system settings.',
    modules: ['Dashboard', 'Transactions', 'Reports', 'Analytics', 'Project Finance', 'Exports'],
    managed_by: 'Organization Head / Admin',
    color: '#0891b2',
  },
  accountant: {
    note: 'Handles daily accounting operations — creating and editing transactions, managing categories. Limited system access.',
    modules: ['Dashboard', 'Transactions', 'Categories', 'Persons', 'Exports'],
    managed_by: 'Organization Head / Admin',
    color: '#059669',
  },
  project_manager: {
    note: 'Manages project-level tasks, team assignments, and project financials. Cannot access organization-wide settings.',
    modules: ['Dashboard', 'Projects', 'Transactions', 'Persons', 'Reports', 'Project Finance'],
    managed_by: 'Organization Head / Admin',
    color: '#d97706',
  },
  employee: {
    note: 'Performs assigned work tasks — views projects, logs work, and sees relevant reports. Read-heavy with limited write access.',
    modules: ['Dashboard', 'Projects', 'Transactions'],
    managed_by: 'Organization Head / Admin',
    color: '#6b7280',
  },
  viewer: {
    note: 'Read-only access to assigned data. Cannot create, edit, or delete anything.',
    modules: ['Dashboard', 'Projects', 'Reports'],
    managed_by: 'Organization Head / Admin',
    color: '#9ca3af',
  },
};

// ─── Module Configuration ──────────────────────────────────────────────────────
export const MODULE_LABELS = {
  dashboard: 'Dashboard',
  users: 'Users',
  roles: 'Roles & Permissions',
  permissions: 'Permissions',
  projects: 'Projects',
  transactions: 'Transactions',
  reports: 'Reports',
  analytics: 'Analytics',
  categories: 'Categories',
  persons: 'Persons',
  invitations: 'Invitations',
  settings: 'Settings',
  exports: 'Exports',
  audit: 'Audit Logs',
  project_finance: 'Project Finance',
};

// ─── Helper Functions ──────────────────────────────────────────────────────────

/**
 * Returns the hierarchy rank for a role (lower = more authority).
 */
export function getRoleRank(role) {
  return ROLE_HIERARCHY[role] ?? 999;
}

/**
 * Returns true when actorRole can manage/configure targetRole.
 */
export function canManageRole(actorRole, targetRole) {
  return getRoleRank(actorRole) < getRoleRank(targetRole);
}

/**
 * Returns all role keys that actorRole is allowed to manage.
 */
export function getAssignableRoles(actorRole) {
  const actorRank = getRoleRank(actorRole);
  return Object.entries(ROLE_HIERARCHY)
    .filter(([, rank]) => rank > actorRank)
    .map(([role]) => role);
}

/**
 * Returns { label, rank, note, modules, managed_by, color } for a role.
 */
export function getRoleInfo(role) {
  return {
    key: role,
    label: ROLE_LABELS[role] ?? role,
    rank: getRoleRank(role),
    ...(ROLE_DETAILS[role] ?? {}),
  };
}
